import {
  Button,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AppointmentStatus } from '@shared/types';
import { FiAlertTriangle } from 'react-icons/fi';

interface StatusConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: AppointmentStatus;
  duration: number;
  onConfirm: () => void;
  previousStatus?: AppointmentStatus | null;
}

/**
 * Modal to confirm appointment status. Shows one of two warnings, based on
 * whether `previousStatus` is provided:
 * - overwrite    → `previousStatus` is set (provider is changing a saved status)
 * - duration-mismatch → `previousStatus` is null/undefined (first save, non-Occurred with non-zero duration)
 */
const StatusConfirmationModal = ({
  isOpen,
  onClose,
  status,
  duration,
  onConfirm,
  previousStatus,
}: StatusConfirmationModalProps): JSX.Element => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        bg="surface.default"
        overflow="hidden"
        borderColor="border.default"
        borderWidth={1}
        borderRadius={10}
        color="text.default"
        maxWidth="500px"
      >
        <ModalHeader as={HStack} mt={5} pb={0} gap={2}>
          <FiAlertTriangle size={20} color="orange" />
          <Heading size="md">Please Confirm Status</Heading>
        </ModalHeader>
        <ModalBody
          as={VStack}
          gap={4}
          position="relative"
          mb={3}
          textAlign="left"
          marginTop={3}
          alignItems="flex-start"
        >
          {previousStatus ? (
            <Text
              color="text.default"
              lineHeight="1.6"
              fontSize="14px"
              data-testid="status-overwrite-warning"
            >
              This appointment is currently saved as{' '}
              <strong>{previousStatus}</strong>. Submitting will overwrite the
              previous result.
            </Text>
          ) : (
            <Text
              color="text.default"
              lineHeight="1.6"
              fontSize="14px"
              data-testid="duration-mismatch-warning"
            >
              You set the status to <strong>{status}</strong> but the meeting
              time was{' '}
              <strong>
                {duration} {duration > 1 ? 'minutes' : 'minute'}
              </strong>
              . Are you sure?
            </Text>
          )}
        </ModalBody>
        <ModalFooter justifyContent="center" mb={6} gap={10}>
          <Button
            variant="outline"
            color="text.default"
            borderColor="border.default"
            onClick={onClose}
            data-testid="cancel-status-button"
            _hover={{
              color: 'green.600',
              bgColor: 'surface.light',
              borderColor: 'green.600',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="white"
            bg="orange.500"
            onClick={handleConfirm}
            data-testid="confirm-status-button"
            _hover={{
              bg: 'orange.600',
            }}
          >
            Yes, I&apos;m sure
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default StatusConfirmationModal;
